import { useEffect, useMemo, useState } from "react";
import { adminFetch } from "./adminApi";

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function AdminContacts() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);

  const pageSize = 20;
  const canPrev = page > 1;
  const canNext = page * pageSize < total;

  const query = useMemo(() => {
    const p = new URLSearchParams();
    p.set("page", String(page));
    p.set("pageSize", String(pageSize));
    if (onlyUnread) p.set("unread", "1");
    return p.toString();
  }, [page, onlyUnread]);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const data = await adminFetch(`/api/admin/contacts?${query}`);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total || 0));
      setUnreadCount(Number(data?.unreadCount || 0));
    } catch (e) {
      setErr(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  async function markRead(id) {
    try {
      await adminFetch(`/api/admin/contacts/${id}/read`, { method: "PATCH" });
      setItems((prev) => prev.map((m) => m.id === id ? { ...m, read: true } : m));
      setUnreadCount((n) => Math.max(0, n - 1));
    } catch (e) {
      alert(e?.message || "Failed");
    }
  }

  async function markAllRead() {
    if (!window.confirm("Mark all messages as read?")) return;
    setMarkingAll(true);
    try {
      await adminFetch("/api/admin/contacts/read-all", { method: "PATCH" });
      await load();
    } catch (e) {
      alert(e?.message || "Failed");
    } finally {
      setMarkingAll(false);
    }
  }

  function toggleExpand(id) {
    setExpanded((prev) => (prev === id ? null : id));
    const msg = items.find((m) => m.id === id);
    if (msg && !msg.read) markRead(id);
  }

  return (
    <div className="mx-auto max-w-4xl px-4 md:px-6 py-10 text-white">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-unbounded text-3xl font-bold">Admin · Contacts</h1>
          <p className="mt-2 text-white/70">
            Messages from the contact form.
            {unreadCount > 0 && (
              <span className="ml-2 inline-flex items-center rounded-full bg-[#7269e3]/30 px-2 py-0.5 text-xs text-[#a89ef5]">
                {unreadCount} unread
              </span>
            )}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => { setOnlyUnread((v) => !v); setPage(1); }}
            className={`rounded-lg px-3 py-2 text-sm ring-1 transition ${
              onlyUnread
                ? "bg-[#7269e3]/20 text-[#a89ef5] ring-[#7269e3]/40"
                : "bg-white/10 text-white/70 ring-white/10 hover:bg-white/15"
            }`}
          >
            {onlyUnread ? "Show all" : "Unread only"}
          </button>

          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              disabled={markingAll}
              className="rounded-lg bg-white/10 px-3 py-2 text-sm ring-1 ring-white/10 hover:bg-white/15 disabled:opacity-50"
            >
              {markingAll ? "Marking…" : "Mark all read"}
            </button>
          )}

          <button onClick={load} className="rounded-lg bg-white/10 px-3 py-2 text-sm ring-1 ring-white/10 hover:bg-white/15">
            Refresh
          </button>
        </div>
      </div>

      <div className="mt-6 space-y-1">
        {/* Pagination header */}
        <div className="flex items-center justify-between px-1 pb-2">
          <div className="text-sm text-white/50">
            {loading ? "Loading…" : `${total} message${total !== 1 ? "s" : ""}`}
          </div>
          <div className="flex items-center gap-2">
            <button disabled={!canPrev} onClick={() => setPage((p) => p - 1)} className="rounded-lg bg-white/10 px-3 py-1.5 text-xs disabled:opacity-40 hover:bg-white/15">Prev</button>
            <span className="text-xs text-white/50">p.{page}</span>
            <button disabled={!canNext} onClick={() => setPage((p) => p + 1)} className="rounded-lg bg-white/10 px-3 py-1.5 text-xs disabled:opacity-40 hover:bg-white/15">Next</button>
          </div>
        </div>

        {err && <div className="rounded-xl bg-red-500/10 px-4 py-3 text-sm text-red-300">{err}</div>}

        {!loading && items.length === 0 && (
          <div className="rounded-2xl bg-white/5 px-6 py-10 text-center text-white/50 ring-1 ring-white/10">
            No messages
          </div>
        )}

        {items.map((msg) => {
          const isOpen = expanded === msg.id;
          return (
            <div
              key={msg.id}
              className={`rounded-2xl ring-1 transition overflow-hidden ${
                msg.read
                  ? "bg-white/[0.03] ring-white/8"
                  : "bg-white/[0.07] ring-[#7269e3]/25"
              }`}
            >
              {/* Header row — always visible */}
              <button
                onClick={() => toggleExpand(msg.id)}
                className="w-full flex items-center gap-4 px-5 py-4 text-left"
              >
                {!msg.read && (
                  <span className="h-2 w-2 shrink-0 rounded-full bg-[#7269e3]" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    <span className="font-semibold text-white text-sm">{msg.name}</span>
                    <span className="text-white/50 text-sm">{msg.email}</span>
                  </div>
                  <div className="mt-0.5 truncate text-sm text-white/60">
                    {msg.message}
                  </div>
                </div>
                <div className="shrink-0 text-xs text-white/40">{timeAgo(msg.createdAt)}</div>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className={`h-4 w-4 shrink-0 text-white/40 transition-transform ${isOpen ? "rotate-180" : ""}`}
                >
                  <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {/* Expanded body */}
              {isOpen && (
                <div className="border-t border-white/8 px-5 pb-5 pt-4">
                  <p className="text-sm leading-7 text-white/80 whitespace-pre-wrap">{msg.message}</p>
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <a
                      href={`mailto:${msg.email}?subject=Re: your message on Profio`}
                      className="rounded-lg bg-[#7269e3] px-4 py-2 text-sm font-semibold text-white hover:brightness-110"
                    >
                      Reply via email
                    </a>
                    <span className="text-xs text-white/40">
                      {new Date(msg.createdAt).toLocaleString()}
                    </span>
                    {!msg.read && (
                      <button
                        onClick={() => markRead(msg.id)}
                        className="rounded-lg bg-white/10 px-3 py-2 text-xs hover:bg-white/15"
                      >
                        Mark read
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
